import React from "react";
import { Box } from "@mui/material";

import {
  Home as HomeIcon,
  AddBoxOutlined as AddBoxIcon,
  ArticleOutlined as FeedIcon,
  LocalShipping as LocalShippingIcon,
  PendingActions as PendingActionsIcon,
  SmartToy as SmartToyIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const SidebarContainer = styled(Box)`
  background-color: transparent;
  padding: 16px 24px;
  margin-top: 75px;
  width: 100%;

  @media (max-width: 768px) {
    display: none !important;
  }
`;

const SidebarContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const TabsRow = styled(Box)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
`;

const SidebarIcon = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: inherit;
  
  svg {
    font-size: 20px;
    color: inherit;
    transition: all 0.2s;
  }
`;

const TabItem = styled(Box)`
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 10px 20px;
  color: #475569;
  height: 42px;
  font-weight: 600;
  font-size: 14px;
  gap: 8px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  justify-content: center;
  cursor: pointer;

  &.active {
    background: #001c4c;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(0, 28, 76, 0.15);
    
    svg {
      color: #ffffff !important;
    }
  }

  &:hover:not(.active) {
    background: #f1f5f9;
    color: #0f172a !important;
    
    svg {
      color: #0f172a !important;
    }
  }
`;

const MobileBottomBar = styled(Box)`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  padding: 0 6px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }
`;

const MobileTabItem = styled(NavLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #64748b;
  font-size: 9px;
  font-weight: 700;
  gap: 4px;
  flex: 1;
  height: 100%;
  transition: all 0.2s ease-in-out;

  &.active {
    color: #001c4c;
    svg {
      color: #001c4c !important;
    }
  }

  svg {
    font-size: 20px;
    color: #64748b;
    transition: all 0.2s ease;
  }
`;

export default function Sidebar({ sideOpen, setSideOpen }) {
  const { i18n, t } = useTranslation("layout");
  const isRTL = i18n.language === "ar";

  return (
    <>
      <SidebarContainer>
        <SidebarContent>
          <TabsRow>
            <TabItem
              component={NavLink}
              to="/"
              activeClassName="active"
            >
              <SidebarIcon>
                <HomeIcon />
              </SidebarIcon>
              <Box sx={{ fontFamily: "Cairo", fontWeight: "bold" }}>
                {t("Sidebar.Menu")}
              </Box>
            </TabItem>
            <TabItem
              component={NavLink}
              to="/reports/shipments"
              activeClassName="active"
            >
              <SidebarIcon>
                <LocalShippingIcon />
              </SidebarIcon>
              <Box sx={{ fontFamily: "Cairo", fontWeight: "600" }}>
                {t("Sidebar.Shipments")}
              </Box>
            </TabItem>
            <TabItem
              component={NavLink}
              to="/new-shipment"
              activeClassName="active"
            >
              <SidebarIcon>
                <AddBoxIcon />
              </SidebarIcon>
              <Box
                sx={{ fontFamily: "Cairo", fontWeight: "600" }}
              >
                {t("Navbar.add_shipment")}
              </Box>
            </TabItem>
            <TabItem component={NavLink} to="/upload" activeClassName="active">
              <SidebarIcon>
                <FeedIcon />
              </SidebarIcon>
              <Box
                sx={{
                  fontFamily: "Cairo",
                  fontWeight: "600",
                }}
              >
                {t("Navbar.upload_excel")}
              </Box>
            </TabItem>
            <TabItem
              component={NavLink}
              to="/new-pickup"
              activeClassName="active"
            >
              <SidebarIcon>
                <AddBoxIcon />
              </SidebarIcon>
              <Box
                sx={{ fontFamily: "Cairo", fontWeight: "600" }}
              >
                {t("Navbar.add_request")}
              </Box>
            </TabItem>
            <TabItem
              component={NavLink}
              to="/reports/export-shipments"
              activeClassName="active"
            >
              <SidebarIcon>
                <FileDownloadIcon />
              </SidebarIcon>
              <Box
                sx={{
                  fontFamily: "Cairo",
                  fontWeight: "600",
                }}
              >
                {t("Sidebar.Export_Shipments")}
              </Box>
            </TabItem>
            <TabItem
              component={NavLink}
              to="/ai-assistant"
              activeClassName="active"
            >
              <SidebarIcon>
                <SmartToyIcon />
              </SidebarIcon>
              <Box sx={{ fontFamily: "Cairo", fontWeight: "700" }}>
                {t("Sidebar.Analysis")}
              </Box>
            </TabItem>
          </TabsRow>
        </SidebarContent>
      </SidebarContainer>

      <MobileBottomBar>
        <MobileTabItem to="/" end>
          <HomeIcon />
          <span>{isRTL ? "الرئيسية" : "Menu"}</span>
        </MobileTabItem>
        <MobileTabItem to="/reports/shipments">
          <LocalShippingIcon />
          <span>{isRTL ? "الشحنات" : "Shipments"}</span>
        </MobileTabItem>
        <MobileTabItem to="/new-shipment">
          <AddBoxIcon />
          <span>{isRTL ? "إضافة" : "Add"}</span>
        </MobileTabItem>
        <MobileTabItem to="/new-pickup">
          <PendingActionsIcon />
          <span>{isRTL ? "طلب" : "Request"}</span>
        </MobileTabItem>
        <MobileTabItem to="/reports/export-shipments">
          <FileDownloadIcon />
          <span>{isRTL ? "تصدير" : "Export"}</span>
        </MobileTabItem>
        <MobileTabItem to="/ai-assistant">
          <SmartToyIcon />
          <span>{isRTL ? "ذكاء" : "AI"}</span>
        </MobileTabItem>
      </MobileBottomBar>
    </>
  );
}
